(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else if (!root.RealmProductHeader) {
    root.RealmProductHeader = api;
    api.mount(root.document, root);
  }
})(typeof window === 'undefined' ? globalThis : window, function () {
  function backgroundColor(data, width, height, surface = [255, 255, 255]) {
    const buckets = new Map();
    for (let y = 0; y < Math.ceil(height * .25); y++) {
      for (let x = 0; x < width; x++) {
        if (x >= Math.ceil(width * .18) && x < Math.floor(width * .82)) continue;
        const offset = (y * width + x) * 4, alpha = data[offset + 3] / 255;
        const color = surface.map((channel, index) => Math.round(data[offset + index] * alpha + channel * (1 - alpha)));
        const key = color.map(channel => Math.floor(channel / 16)).join(',');
        if (!buckets.has(key)) buckets.set(key, []);
        buckets.get(key).push(color);
      }
    }
    const colors = [...buckets.values()].sort((a, b) => b.length - a.length)[0];
    if (!colors?.length) return null;
    return [0, 1, 2].map(index => colors.map(color => color[index]).sort((a, b) => a - b)[Math.floor(colors.length / 2)]);
  }
  function lightControls(color) {
    const luminance = rgb => rgb.map(value => {
      const channel = value / 255;
      return channel <= .04045 ? channel / 12.92 : ((channel + .055) / 1.055) ** 2.4;
    }).reduce((sum, channel, index) => sum + channel * [.2126, .7152, .0722][index], 0);
    const darkContrast = (luminance(color) + .05) / .05;
    const lightContrast = 1.05 / (luminance(color.map(value => .72 * value + .28 * 255)) + .05);
    return lightContrast > darkContrast;
  }
  function mount(document, window) {
    if (!document.body.classList.contains('realm-page--product')) return;
    const mobile = window.matchMedia('(max-width: 1024px)'), cache = new Map();
    let owner = null, observer = null, frame = null, version = 0;
    const clear = () => {
      document.body.style.removeProperty('--realm-product-header-rgb');
      delete document.body.dataset.realmProductHeader;
      delete document.body.dataset.realmProductHeaderLight;
    };
    const sample = image => {
      const canvas = document.createElement('canvas');
      canvas.width = 48; canvas.height = 48;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) return null;
      context.drawImage(image, 0, 0, 48, 48);
      let surface = [255, 255, 255], element = image.parentElement;
      while (element) {
        const color = window.getComputedStyle(element).backgroundColor.match(/^rgba?\(([^)]+)\)$/);
        if (color) {
          const channels = color[1].split(/[,\s/]+/).filter(Boolean).map(Number);
          if (channels.length === 3 || channels[3] === 1) { surface = channels.slice(0, 3); break; }
        }
        element = element.parentElement;
      }
      return backgroundColor(context.getImageData(0, 0, 48, 48).data, 48, 48, surface);
    };
    const apply = (color, token) => {
      if (!color || token !== version || !mobile.matches || !owner?.isConnected) return;
      document.body.style.setProperty('--realm-product-header-rgb', color.join(','));
      document.body.dataset.realmProductHeader = 'true';
      document.body.dataset.realmProductHeaderLight = String(lightControls(color));
    };
    const update = () => {
      frame = null;
      const token = ++version;
      if (!mobile.matches || !owner?.isConnected) { clear(); return; }
      const slides = owner.querySelectorAll('[data-realm-gallery] .realm-gallery-slide');
      const index = Number(owner.dataset.galleryActiveIndex) || 0;
      const image = slides[index]?.querySelector('img');
      if (!image || !image.complete || !image.naturalWidth) { clear(); return; }
      const source = image.currentSrc || image.src;
      if (cache.has(source)) { apply(cache.get(source), token); return; }
      try {
        const color = sample(image);
        if (color) { cache.set(source, color); apply(color, token); }
        else clear();
      } catch (_) {
        clear();
        const preview = new window.Image();
        preview.crossOrigin = 'anonymous';
        preview.onload = () => {
          try {
            const color = sample(preview);
            if (color) { cache.set(source, color); apply(color, token); }
          } catch (_) {}
        };
        const url = new URL(source, window.location.href);
        url.searchParams.set('width', '96');
        preview.src = url.href;
      }
    };
    const schedule = () => {
      ++version;
      if (frame === null) frame = window.requestAnimationFrame(update);
    };
    const bind = () => {
      observer?.disconnect();
      owner = document.querySelector('#MainContent [data-realm-product]');
      if (owner && window.MutationObserver) {
        observer = new window.MutationObserver(schedule);
        observer.observe(owner, { attributes: true, attributeFilter: ['data-gallery-active-index'] });
      }
      schedule();
    };
    document.addEventListener('load', event => {
      if (event.target.matches?.('[data-realm-gallery] .realm-gallery-slide img')) schedule();
    }, true);
    document.addEventListener('error', event => {
      if (event.target.matches?.('[data-realm-gallery] .realm-gallery-slide img')) schedule();
    }, true);
    document.addEventListener('shopify:section:load', bind);
    document.addEventListener('shopify:section:unload', event => {
      if (!owner || !event.target.contains(owner)) return;
      ++version;
      observer?.disconnect(); clear();
      if (frame !== null) window.cancelAnimationFrame(frame);
      frame = null;
    });
    mobile.addEventListener('change', schedule);
    window.addEventListener('pageshow', bind);
    window.addEventListener('pagehide', () => {
      ++version; observer?.disconnect();
      if (frame !== null) window.cancelAnimationFrame(frame);
      frame = null;
    });
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bind, { once: true });
    else bind();
    return { update, bind };
  }
  return { backgroundColor, lightControls, mount };
});
